"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Instant = void 0;
exports.instantOf = instantOf;
const misc_1 = require("@moojo/misc");
const date_fns_1 = require("date-fns");
const zod_1 = require("zod");
const duration_1 = require("./duration");
/**
 * An immutable point in time, in milliseconds granularity.
 */
class Instant {
    /**
     * @returns the instant representing 1970-01-01T00:00:00Z
     */
    static get epoch() {
        return epoch;
    }
    /**
     * Number of milliseconds since 1-1-1970 (UTC).
     */
    millis;
    constructor(input) {
        if (!(0, date_fns_1.isValid)(input)) {
            throw new Error(`Invalid Instant input: ${input}`);
        }
        this.millis = new Date(input).getTime();
    }
    static fromMillis(millisSince1970) {
        return new Instant(millisSince1970);
    }
    // #TD(imaman): unite all the from() methods into a single one.
    static fromIsoString(iso8601Timestamp) {
        const date = (0, date_fns_1.parseISO)(iso8601Timestamp);
        if (!(0, date_fns_1.isValid)(date)) {
            throw new Error(`Invalid ISO-8601 timestamp: ${iso8601Timestamp}`);
        }
        return new Instant(date);
    }
    static fromDate(date) {
        return new Instant(date);
    }
    /**
     * @returns a Date object matching the instant.
     */
    get date() {
        return new Date(this.millis);
    }
    /**
     * @returns a duration from epoch to this instant
     */
    get duration() {
        return this.minus(epoch);
    }
    /**
     * @returns Number of seconds since 1-1-1970 (UTC).
     */
    get seconds() {
        return Math.round(this.millis / 1000);
    }
    /**
     * @returns the ISO-8601 representation of the instant.
     */
    format() {
        return this.date.toISOString();
    }
    toJSON() {
        return this.millis;
    }
    toString() {
        return this.format();
    }
    static zod = (units = 'millis') => {
        let toMillis;
        if (units === 'millis') {
            toMillis = 1;
        }
        else if (units === 'seconds') {
            toMillis = 1000;
        }
        else {
            (0, misc_1.shouldNeverHappen)(units);
        }
        return zod_1.z.union([
            zod_1.z.instanceof(Instant),
            zod_1.z.number().transform(n => {
                return Instant.fromMillis(n * toMillis);
            }),
            zod_1.z.string().transform(s => Instant.fromIsoString(s)),
            zod_1.z.object({ millis: zod_1.z.number() }).transform(o => Instant.fromMillis(o.millis)),
        ]);
    };
    is(operator, other) {
        const c = this.compare(other);
        return (0, misc_1.switchOn)(operator, {
            '<': () => c < 0,
            '<=': () => c <= 0,
            '==': () => c == 0,
            '>=': () => c >= 0,
            '>': () => c > 0,
        });
    }
    compare(other) {
        if (this.millis < other.millis) {
            return -1;
        }
        if (this.millis > other.millis) {
            return 1;
        }
        return 0;
    }
    shiftBy(...a) {
        const d = a.length === 1 ? a[0] : (0, duration_1.durationOf)(a[0], a[1]);
        const combined = this.duration.plus(d);
        return instantOf(combined);
    }
    /**
     * Computes the difference between `this` and `rhs`
     * @param rhs the Insant to subtract
     * @returns The duration between `rhs` and `this`
     */
    minus(rhs) {
        const diff = this.millis - rhs.millis;
        return (0, duration_1.durationOf)(diff, 'millis');
    }
    static max(first, ...others) {
        let maxInstant = first;
        for (const other of others) {
            maxInstant = maxInstant.millis >= other.millis ? maxInstant : other;
        }
        return maxInstant;
    }
    static min(first, ...others) {
        let minInstant = first;
        for (const other of others) {
            minInstant = minInstant.millis <= other.millis ? minInstant : other;
        }
        return minInstant;
    }
}
exports.Instant = Instant;
function instantOf(input) {
    if (input instanceof Instant) {
        return input;
    }
    input = input instanceof duration_1.Duration ? input.whole('millis') : input;
    return Instant.fromDate(new Date(input));
}
const epoch = instantOf(0);
//# sourceMappingURL=data:application/json;base64,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